/* eslint-disable @typescript-eslint/no-require-imports */
import { DomainConfigs, SiteInfo } from "../types";
import { resolveStripePublishableKey } from "../helpers/stripePublishableKey";
import { fonts as mojitoFonts } from "@/themes/mojito/fonts/fonts";
import { fonts as daikiriFonts } from "@/themes/daikiri/fonts/fonts";
import { fonts as kalimotxoFonts } from "@/themes/kalimotxo/fonts/fonts";
import { fonts as negroniFonts } from "@/themes/negroni/fonts/fonts";

import { THEME } from "@/themes/types";
import { onImportFonts } from "../fonts/utils";
const { indexedPaths } = require("../seo.ts");

const domainConfig: Record<string, DomainConfigs> = {
  "qrcreator.com": {
    siteName: "QR Creator",
    theme: "mojito",
    stripeSiteAccount: "qrcreator",
    isFreeTrial: false,
    companies: [
      "ebdaniTradeSL",
      "globalOpServicesLLC",
      "onworldDigitalCorporationSL",
      "luxorGlobalTechLLC",
      "trascenderNXGlobalLLC",
      "talentAdvisersServicesSL",
    ],
    indexedPathsKey: "qrcreator.com",
    fonts: mojitoFonts,
    scanDomain: "qrcreator.dev",
    enableLandingSEMPages: true,
    showTrustpilot: true,
    showImpressum: true,
    showTokuteiShotorihiki: true,
    seoPricingPage: false,
    showDashboardSpanBox: true,
    roundedAuthButtons: false,
    showAlertForRegisteredUsers: true,
  },
  "fastqrgenerator.com": {
    siteName: "Fast QR Generator",
    theme: "daikiri",
    isFreeTrial: true,
    companies: ["onworldDigitalCorporationSL", "globalOpServicesLLC", "ebdaniTradeSL"],
    indexedPathsKey: "fastqrgenerator.com",
    fonts: daikiriFonts,
    enableLandingSEMPages: true,
    showTrustpilot: false,
    seoPricingPage: false,
    showDashboardSpanBox: false,
    roundedAuthButtons: false,
    showAlertForRegisteredUsers: false,
  },
  "onlineqrcode.com": {
    siteName: "onlineQRcode",
    theme: "kalimotxo",
    isFreeTrial: false,
    companies: ["ebdaniTradeSL", "globalOpServicesLLC", "onworldDigitalCorporationSL"],
    indexedPathsKey: "onlineqrcode.com",
    fonts: kalimotxoFonts,
    downloadQRRedirectionPath: "/my-qr-codes",
    enableLandingSEMPages: false,
    showTrustpilot: false,
    seoPricingPage: true,
    showDashboardSpanBox: false,
    roundedAuthButtons: false,
    showAlertForRegisteredUsers: false,
  },
  "qrnow.com": {
    siteName: "QR Now",
    theme: "negroni",
    stripeSiteAccount: "qrnow",
    isFreeTrial: false,
    companies: [
      "starlightInnovationsLLC",
      "luxorGlobalTechLLC",
      "trascenderNXGlobalLLC",
      "talentAdvisersServicesSL",
    ],
    indexedPathsKey: "qrnow.com",
    fonts: negroniFonts,
    scanDomain: "qrnow.pro",
    enableLandingSEMPages: true,
    showTrustpilot: false,
    seoPricingPage: true,
    showDashboardSpanBox: true,
    roundedAuthButtons: true,
    showAlertForRegisteredUsers: false,
  },
  localhost: {
    siteName: "QR Creator Local",
    theme: process.env.NEXT_PUBLIC_THEME as THEME,
    isFreeTrial: false,
    companies: ["ebdaniTradeSL"],
    indexedPathsKey: "onlineqrcode.com",
    fonts: kalimotxoFonts,
    enableLandingSEMPages: false,
    showTrustpilot: false,
    seoPricingPage: true,
    showDashboardSpanBox: false,
    roundedAuthButtons: false,
    showAlertForRegisteredUsers: false,
  },
};

/**
 * Class Factory to generate site configuration.
 */
class SiteConfigFactory {
  /**
   * Creates a complete domain configuration using domain specifications.
   * @param domainName site domain (Ex. "qrcreator.com").
   * @returns the complete site configuration.
   */

  public static create(domainName: string): SiteInfo {
    const config = domainConfig[domainName];

    if (!config) {
      throw new Error(`Configuration not found for the domain: ${domainName}`);
    }

    const baseConfig = {
      siteWhiteLogo: "/svg/logoFooter.svg",
      siteBlackLogo32: "/svg/logo32.svg",
      siteBlackLogo28: "/svg/logo28.svg",
      siteLogoIcon: "/svg/logoIcon.svg",
      siteBlackLogoFooter: "/svg/logoFooter.svg",
      favicon: "/images/favicon-default.png",
      siteLogoQROnly: "/svg/logoQROnly.svg",
      siteEmail: `info@${domainName}`,
    };

    const stripePublishableKey = resolveStripePublishableKey(config.stripeSiteAccount);

    const finalSiteConfig: SiteInfo = {
      siteWhiteLogo: baseConfig.siteWhiteLogo,
      siteBlackLogo32: baseConfig.siteBlackLogo32,
      siteBlackLogo28: baseConfig.siteBlackLogo28,
      siteLogoIcon: baseConfig.siteLogoIcon,
      siteBlackLogoFooter: baseConfig.siteBlackLogoFooter,
      siteLogoQROnly: baseConfig.siteLogoQROnly,
      favicon: baseConfig.favicon,
      siteEmail: baseConfig.siteEmail,
      domainName: domainName,
      siteTheme: config.theme,
      siteName: config.siteName,
      isFreeTrial: config.isFreeTrial,
      companies: config.companies,
      indexedPaths: indexedPaths[config.indexedPathsKey],
      fonts: onImportFonts(config.fonts),
      abTestEnabled: config?.abTestEnabled ?? false,
      downloadQRRedirectionPath: config.downloadQRRedirectionPath,
      enableLandingSEMPages: config.enableLandingSEMPages ?? true,
      showTrustpilot: config.showTrustpilot ?? false,
      showImpressum: config.showImpressum ?? false,
      showTokuteiShotorihiki: config.showTokuteiShotorihiki ?? false,
      seoPricingPage: config.seoPricingPage ?? false,
      showDashboardSpanBox: config.showDashboardSpanBox ?? false,
      roundedAuthButtons: config.roundedAuthButtons ?? false,
      showAlertForRegisteredUsers: config.showAlertForRegisteredUsers ?? false,
      homeLayoutOverride: config.homeLayoutOverride ?? "legacy",
      ...(config.scanDomain && { scanDomain: config.scanDomain }),
      ...(stripePublishableKey ? { stripePublishableKey } : {}),
    };

    return finalSiteConfig;
  }
}

export { SiteConfigFactory };
